import { CancelOptions, QueryClient, QueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { cancelTellescopeQueries } from "./cancel-tellescope-queries";

/**
 * Arguments for canceling all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 */
export interface CancelTellescopeModelQueriesArgs<Model extends ModelName, TError = Error> {
  /** The Tellescope model to cancel queries for */
  model: Model;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /**
   * Optional filters applied to all queries for this model.
   * These filters are combined with method-specific filters if both are provided.
   */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TError,
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TellescopeQueryKey<Model, QueryMethodForModel<Model>>
    >,
    "queryKey"
  >;
  /** Optional cancel options applied to all cancellations */
  options?: CancelOptions;
  /**
   * Optional method-specific filters. Allows canceling queries for specific methods
   * and optionally matching by request parameters.
   */
  methodFilters?: Partial<{
    [Method in QueryMethodForModel<Model>]: Omit<
      TellescopeQueryFilters<
        QueryDataForMethod<Model, Method>,
        TError,
        QueryDataForMethod<Model, Method>,
        TellescopeQueryKey<Model, Method>
      >,
      "queryKey"
    > & {
      /** Optional request parameters to match for this method */
      request?: Partial<ParamsForMethod<Model, Method>>;
      /** Optional method-specific cancel options that override global options */
      options?: CancelOptions;
    };
  }>;
}

/**
 * Cancels all outgoing queries for a specific Tellescope model.
 *
 * This function provides a way to cancel all queries for a model, optionally
 * with method-specific filters. When no methodFilters are provided, it cancels
 * all queries for the model. When methodFilters are provided, it only cancels
 * queries for the specified methods.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 *
 * @param args - Configuration object containing model, queryClient, and optional filters
 * @returns A promise that resolves when cancellation is complete
 *
 * @example
 * ```ts
 * // Cancel all user queries
 * await cancelTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 * });
 * ```
 *
 * @example
 * ```ts
 * // Cancel only specific methods
 * await cancelTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   methodFilters: {
 *     getOne: {
 *       // Cancel all getOne queries
 *     },
 *     getSome: {
 *       request: { filter: { fname: "John" }, limit: 10 },
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global filters with method-specific filters
 * await cancelTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   filters: {
 *     fetchStatus: "fetching", // Only cancel fetching queries
 *   },
 *   methodFilters: {
 *     getOne: {
 *       // Inherits fetchStatus: "fetching" from global filters
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global and method-specific options
 * await cancelTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   options: {
 *     revert: true, // Global option for all methods
 *   },
 *   methodFilters: {
 *     getOne: {
 *       options: {
 *         silent: true, // Method-specific option (overrides global)
 *       },
 *     },
 *   },
 * });
 * ```
 */
export const cancelTellescopeModelQueries = async <Model extends ModelName, TError = Error>({
  model,
  queryClient,
  filters,
  methodFilters,
  options,
}: CancelTellescopeModelQueriesArgs<Model, TError>) => {
  const methodNames = Object.keys(methodFilters ?? {}) as QueryMethodForModel<Model>[];

  // If no method filters are specified, cancel all queries for the model
  if (!methodFilters || !methodNames.length) {
    return queryClient.cancelQueries(
      {
        queryKey: [model],
        exact: false,
        ...filters,
      } as QueryFilters<Model[]>,
      options
    );
  }

  // Otherwise, cancel queries for each specified method
  const cancelPromises = methodNames.map(<Method extends QueryMethodForModel<Model>>(method: Method) => {
    const { request, options: methodOptions, ...methodSpecificFilters } = methodFilters[method] ?? {};

    return cancelTellescopeQueries<Model, Method, TError>({
      model,
      method,
      request,
      queryClient,
      filters: {
        ...filters,
        ...methodSpecificFilters,
      },
      options: { ...options, ...methodOptions },
    });
  });

  await Promise.all(cancelPromises);
};
